import React, { useState } from 'react'
import styled from 'styled-components'
import Modal from 'components/_UI/Modal'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import Fieldset from 'components/_UI/Fieldset'
import ImageUpload from 'components/_UI/ImageUpload'
import { useForm } from 'react-hook-form'
import { useEvent } from 'context/events/Event'
import fieldValidations from 'helpers/fieldValidations'
import sponsorSectionsApi from 'api/requests/sponsorSections'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import { Column } from 'styled/alignment/Column'

const socialFields = [
    { name: 'instagram', label: 'Instagram' },
    { name: 'facebook', label: 'Facebook' },
    { name: 'twitter', label: 'Twitter/X' },
    { name: 'youtube', label: 'YouTube' },
    { name: 'tiktok', label: 'TikTok' },
    { name: 'website', label: 'Site' },
]

// Aceita URL com ou sem protocolo, sem limitar o tamanho do TLD (helpers/regex.js#link
// tem a flag "g" e reaproveitá-la em vários campos do mesmo form causa falsos negativos,
// pois o lastIndex fica compartilhado entre as chamadas de .test()).
const URL_PATTERN = /^(https?:\/\/)?([a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(:\d+)?([/?#]\S*)?$/i

function optionalUrlValidation(label) {
    return {
        validate: (value) => !value || URL_PATTERN.test(value.trim()) || `Informe uma URL válida para ${label}.`
    }
}

function SponsorItemForm({ modalItem, setModalItem, sectionId, itemLabel = 'item', toast, onSuccess, edit = false }) {
    const { event } = useEvent()
    const [loading, setLoading] = useState(false)
    const [logoFile, setLogoFile] = useState(null)
    const [logoPreview, setLogoPreview] = useState(edit?.logo_url || null)

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: edit?.name,
            link: edit?.link,
            instagram: edit?.social_links?.instagram,
            facebook: edit?.social_links?.facebook,
            twitter: edit?.social_links?.twitter,
            youtube: edit?.social_links?.youtube,
            tiktok: edit?.social_links?.tiktok,
            website: edit?.social_links?.website,
        }
    })

    function handleImageUpload(files) {
        if (files?.length) {
            const file = files[0]
            setLogoFile(file)
            setLogoPreview(file.preview || URL.createObjectURL(file))
        }
    }

    function handleImageDelete() {
        setLogoFile(null)
        setLogoPreview(null)
    }

    function createPayload(content) {
        return {
            name: content.name,
            link: content.link || null,
            social_links: {
                instagram: content.instagram || null,
                facebook: content.facebook || null,
                twitter: content.twitter || null,
                youtube: content.youtube || null,
                tiktok: content.tiktok || null,
                website: content.website || null,
            }
        }
    }

    async function uploadLogoIfNeeded(itemId) {
        if (!logoFile) return true

        try {
            const formData = new FormData()
            formData.append('logo', logoFile)
            await sponsorSectionsApi.uploadItemLogo(event.slug, sectionId, itemId, formData)
            return true
        } catch (error) {
            toast?.error('O item foi salvo, mas não foi possível enviar a logo. Tente novamente editando o item.', { position: 'top-center' })
            return false
        }
    }

    async function createItem(content) {
        setLoading(true)
        const payload = createPayload(content)

        try {
            const { data } = await sponsorSectionsApi.createItem(event.slug, sectionId, payload)
            await uploadLogoIfNeeded(data?.data?.id)
            onSuccess('createItem')
        } catch (error) {
            toast?.error('Não foi possível salvar o item. Verifique os dados e tente novamente.', { position: 'top-center' })
        } finally {
            setLoading(false)
        }
    }

    async function editItem(content) {
        setLoading(true)
        const payload = createPayload(content)

        try {
            await sponsorSectionsApi.updateItem(event.slug, sectionId, edit.id, payload)
            await uploadLogoIfNeeded(edit.id)
            onSuccess('editItem')
        } catch (error) {
            toast?.error('Não foi possível atualizar o item. Verifique os dados e tente novamente.', { position: 'top-center' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={!!modalItem} onClose={() => setModalItem(false)} childrenOverflowY='scroll'>
            <Text name='highlight'>{!edit ? 'Adicionar' : 'Atualizar'} {itemLabel}</Text>
            <Form onSubmit={handleSubmit(!edit ? createItem : editItem)}>
                <Fieldset
                    label='Nome *'
                    placeholder='Nome do patrocinador'
                    register={register}
                    name='name'
                    layout='squired'
                    validations={fieldValidations.generic('nome', 2, 150)}
                />
                {errors?.name?.message && (
                    <Column>
                        <Text color={colors.danger} name='small'>{errors.name.message}</Text>
                    </Column>
                )}

                <LogoWrap>
                    <Text paddingBottom={'5px'}>Logo</Text>
                    <ImageUpload
                        onToggleFile={handleImageUpload}
                        onDeleteFile={handleImageDelete}
                        isEdit={logoPreview ? { image: logoPreview } : null}
                        product
                    />
                </LogoWrap>

                <FieldWrap>
                    <Fieldset
                        label='Link do logo'
                        placeholder='https://...'
                        register={register}
                        name='link'
                        layout='squired'
                        validations={optionalUrlValidation('o link')}
                    />
                    {errors?.link?.message && (
                        <Column>
                            <Text color={colors.danger} name='small'>{errors.link.message}</Text>
                        </Column>
                    )}
                </FieldWrap>

                <Text paddingBottom={'5px'} marginTop={'15px'}>Redes sociais</Text>
                {socialFields.map((field) => (
                    <FieldWrap key={field.name}>
                        <Fieldset
                            label={field.label}
                            placeholder='https://...'
                            register={register}
                            name={field.name}
                            layout='squired'
                            validations={optionalUrlValidation(field.label)}
                        />
                        {errors?.[field.name]?.message && (
                            <Column>
                                <Text color={colors.danger} name='small'>{errors[field.name].message}</Text>
                            </Column>
                        )}
                    </FieldWrap>
                ))}

                {loading ? (
                    <Loader />
                ) : (
                    <ContentButton>
                        <Button
                            variant={'light'}
                            category={'roundedBorded'}
                            onClick={() => setModalItem(false)}
                        >
                            Cancelar
                        </Button>
                        <Button variant='featured' type={'submit'}>
                            {!edit ? 'Adicionar' : 'Atualizar'}
                        </Button>
                    </ContentButton>
                )}
            </Form>
        </Modal>
    )
}

export default SponsorItemForm

const Form = styled.form`
    width: 100%;
`;

const LogoWrap = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 15px;
`;

const FieldWrap = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 15px;
`;

const ContentButton = styled(Column)`
    margin-top: 15px;
    @media screen and (min-width: ${breakpoints.md}){
        flex-direction: row;
        gap: 5px;
    }
`;
